/**
 * Activity Block Task Queue — click a block to work Call → Disposition → Complete → Next.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { CATEGORY_META } from './plannerConstants';
import { TaskCard } from '../tasks/TaskCard';
import { CompleteTaskModal } from '../tasks/CompleteTaskModal';
import { Trash2 } from 'lucide-react';

export function BlockDetailModal({ block, onClose, onChanged }) {
  const [completingTask, setCompletingTask] = useState(null);
  if (!block) return null;
  const meta = CATEGORY_META[block.activity_category] || { label: block.activity_category };

  const removeTask = async (taskId) => {
    try {
      await api.unassignTaskFromBlock(block.id, taskId);
      toast.success('Removed from block');
      onChanged();
    } catch (e) {
      toast.error('Failed to remove task');
    }
  };

  const snoozeTask = async (task, option) => {
    try {
      await api.snoozeTask(task.id, { option });
      toast.success(`Snoozed — ${option.replace(/_/g, ' ')}`);
      onChanged();
    } catch (e) {
      toast.error('Failed to snooze task');
    }
  };

  const deleteBlock = async () => {
    try {
      await api.deletePlannerBlock(block.id);
      toast.success('Block deleted — tasks returned to tray');
      onChanged();
      onClose();
    } catch (e) {
      toast.error('Failed to delete block');
    }
  };

  return (
    <>
      <Dialog open={!!block} onOpenChange={(v) => !v && onClose()}>
        <DialogContent data-testid="block-detail-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{meta.label} — {new Date(block.start).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</DialogTitle>
            <DialogDescription>Work through the tasks queued in this activity block.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(!block.tasks || block.tasks.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No tasks queued in this block yet — drag one in from the tray.</p>
            ) : (
              block.tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <TaskCard task={t} onComplete={setCompletingTask} onSnooze={snoozeTask} />
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeTask(t.id)} data-testid={`block-remove-task-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="justify-between sm:justify-between">
            <Button variant="outline" className="text-red-500 hover:text-red-600" onClick={deleteBlock} data-testid="block-delete-btn">
              Delete Block
            </Button>
            <Button onClick={onClose} data-testid="block-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CompleteTaskModal task={completingTask} onClose={() => setCompletingTask(null)} onDone={onChanged} />
    </>
  );
}
